import { useEffect, useMemo, useRef, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import { withNamespaces } from 'react-i18next';
import { AvField, AvForm } from 'availity-reactstrap-validation';
import { Button, Spinner } from 'reactstrap';

import useMailboxRoleAccess from 'src/modules/mailer/hooks/useMailboxRoleAccess';
import useMailboxActions from '../../hooks/useMailboxActions';

import ThreadNode from './components/ThreadNode';
import {
    buildCaseThreadTree,
    getConversationSubject,
    listThreadMessages,
} from './helpers/threadTree';
import useCaseMailboxThread from './hooks/useCaseMailboxThread';

import './style.scss';

const CaseMailboxThread = ({
    t,
    caseId,
    report,
    isDeleted = false,
    prefillHtml = null,
    onPrefillConsumed,
}) => {
    const [isReplyOpen, setIsReplyOpen] = useState(false);
    const [openIds, setOpenIds] = useState([]);
    const isReadOnly = Boolean(report?.is_read_only_analyst || isDeleted);
    const { canSyncMailbox } = useMailboxRoleAccess();
    const { syncMailbox } = useMailboxActions({ t });
    const syncMailboxRef = useRef(syncMailbox);
    syncMailboxRef.current = syncMailbox;
    const {
        items,
        requesterEmail,
        defaultSubject,
        formKey,
        prefillBody,
        isLoading,
        isSending,
        handleSubmit,
    } = useCaseMailboxThread({
        t,
        caseId,
        report,
        prefillHtml,
        onPrefillConsumed,
        onSent: () => setIsReplyOpen(false),
    });
    const thread = useMemo(() => buildCaseThreadTree(items), [items]);
    const messages = useMemo(() => listThreadMessages(thread), [thread]);
    const conversationSubject = useMemo(() => getConversationSubject(thread), [thread]);
    const newestKey = messages[0]?.key || '';

    useEffect(() => {
        if (!canSyncMailbox || !caseId) {
            return undefined;
        }

        syncMailboxRef.current({ silent: true });

        return undefined;
    }, [canSyncMailbox, caseId]);

    useEffect(() => {
        if (prefillHtml) {
            setIsReplyOpen(true);
        }
    }, [prefillHtml]);

    useEffect(() => {
        setOpenIds((current) => {
            if (!newestKey) {
                return current.length ? [] : current;
            }

            if (current.length === 1 && current[0] === newestKey) {
                return current;
            }

            return [newestKey];
        });
    }, [newestKey]);

    if (isLoading) {
        return (
            <div className="text-center py-4">
                <Spinner color="primary" />
            </div>
        );
    }

    return (
        <div className="case-mailbox-thread">
            {!thread.length ? (
                <p className="text-muted">
                    {t('No emails are linked to this case yet.')}
                </p>
            ) : (
                <>
                    <div className="case-mailbox-thread__title">
                        {conversationSubject || t('(No subject)')}
                    </div>
                    <Accordion
                        alwaysOpen
                        activeKey={openIds}
                        onSelect={(keys) => setOpenIds(Array.isArray(keys) ? keys : [])}
                        className="d-flex flex-column gap-3 mb-4"
                    >
                        {messages.map((node) => (
                            <ThreadNode
                                key={node.key}
                                node={node}
                                isOpen={openIds.includes(node.key)}
                                t={t}
                            />
                        ))}
                    </Accordion>
                </>
            )}

            {isReadOnly ? null : (
                <>
                    <Button
                        type="button"
                        color="primary"
                        outline
                        className="mb-3"
                        onClick={() => setIsReplyOpen((open) => !open)}
                    >
                        <i className="ri-reply-line align-middle me-1" />
                        {items.length ? t('Reply') : t('Send first email')}
                    </Button>

                    {isReplyOpen ? (
                        <AvForm
                            key={formKey}
                            className="needs-validation"
                            model={{
                                to: requesterEmail || '',
                                subject: defaultSubject,
                                body: prefillBody,
                            }}
                            onValidSubmit={handleSubmit}
                        >
                            <AvField
                                name="to"
                                label={t('To')}
                                type="text"
                                className="form-control mb-3"
                                required
                            />
                            <AvField
                                name="subject"
                                label={t('Subject')}
                                type="text"
                                className="form-control mb-3"
                            />
                            <AvField
                                name="body"
                                label={t('Message')}
                                type="textarea"
                                rows="8"
                                className="form-control mb-3"
                            />
                            <Button
                                color="primary"
                                type="submit"
                                disabled={isSending}
                            >
                                {isSending ? <Spinner size="sm" /> : t('Send')}
                            </Button>
                        </AvForm>
                    ) : null}
                </>
            )}
        </div>
    );
};

export default withNamespaces()(CaseMailboxThread);
